import type {AttributeValue} from './attribute'
import type {SetAttributeRequest} from './attribute-definition'
import type {Instant, UUID} from './common'
import type {EntityLink} from './entity-link'
import type {Source} from './source'
/* eslint-disable i18next/no-literal-string -- const definitions are the source of truth */

import {BadgeVariant} from '@/components/ui/badge'
import {EnumValue} from '@/components/shared/EnumValue'
import {CheckCircle, AlertCircle, XCircle, type LucideIcon} from 'lucide-react'

export const IdentityLifecycleState = {
    ACTIVE: 'ACTIVE',
    SUSPENDED: 'SUSPENDED',
    TERMINATED: 'TERMINATED',
} as const;
export type IdentityLifecycleState = typeof IdentityLifecycleState[keyof typeof IdentityLifecycleState];

/**
 * Subset of {@link IdentityLifecycleState} values that can appear as the target
 * of a lifecycle action button. Currently identical to the wire enum; kept as
 * a distinct alias so additional non-action states (if any) can be excluded
 * later without touching every call site.
 */
export type IdentityLifecycleActionTarget =
    | typeof IdentityLifecycleState.ACTIVE
    | typeof IdentityLifecycleState.SUSPENDED
    | typeof IdentityLifecycleState.TERMINATED;

/**
 * Narrow list-payload DTO for identities. Mirrors backend {@code IdentityListItem}
 * — rendered by the identities list page (party, identity type, lifecycle state,
 * derived display name, entry / exit dates, created-at). The detail page consumes
 * the wider {@link Identity} shape (extending this interface).
 */
export interface IdentityListItem {
  id: UUID;
  party: EntityLink;
  identityType: string;
  lifecycleState: IdentityLifecycleState;
  derivedDisplayName: string | null;
  entryDate: Instant | null;
  exitDate: Instant | null;
  createdAt: Instant;
}

/**
 * Wide detail-payload DTO for identities. Mirrors backend
 * {@code IdentityDetail extends IdentityListItem} — adds the fields the detail
 * page renders on top of the list shape (lifecycleChangedAt, reactivationDate,
 * sources, version, updatedAt).
 *
 * {@code sources} carries the {@code identity_sources} rows for this entity (Spec §6 — added
 * in 572.05). Empty when the identity was created manually and is not synced from any
 * external system. Never undefined — the API always serializes it (possibly as `[]`).
 */
export interface Identity extends IdentityListItem {
  lifecycleChangedAt: Instant | null;
  reactivationDate: Instant | null;
  sources: Source[];
  version: number;
  updatedAt: Instant;
}

/**
 * Request to create a new identity.
 *
 * Issue 727 — attributes are required at create time so the row insert,
 * attribute writes, and displayName derivation all run atomically in the same
 * backend transaction. Missing required attributes return 400.
 */
export interface IdentityCreateRequest {
  partyId: UUID;
  identityType: string;
  entryDate?: Instant | null;
  exitDate?: Instant | null;
  attributes?: SetAttributeRequest[];
}

/**
 * Request to change identity lifecycle state.
 * {@code reactivationDate} is optional and only honoured on transitions to SUSPENDED.
 */
export interface IdentityLifecycleRequest {
  lifecycleState: IdentityLifecycleState;
  version: number;
  reactivationDate?: string | null;
  reason?: string | null;
}

/**
 * Request to update (or clear) a SUSPENDED identity's planned reactivation date.
 */
export interface UpdateReactivationDateRequest {
  reactivationDate?: string | null;
  version: number;
}

/**
 * Request to create a new identifier.
 */
export interface CreateIdentifierRequest {
  identifierType: string;
  identifierValue: string;
  primary: boolean;
}

/**
 * Request to update an existing identifier.
 */
export interface UpdateIdentifierRequest {
  identifierValue?: string;
  primary?: boolean;
}

/**
 * Request to update identity entry date.
 */
export interface UpdateEntryDateRequest {
  entryDate?: string;
  version: number;
}

/**
 * Request to update identity exit date.
 */
export interface UpdateExitDateRequest {
  exitDate?: string;
  version: number;
}

/**
 * Request to rehire a TERMINATED identity.
 */
export interface RehireRequest {
  newEntryDate: string;
  reason: string;
  version: number;
}

/**
 * Paginated response for identities.
 */
export interface IdentityPage {
  content: Identity[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

/**
 * Inline {@code toIdentity} sub-object on an IdentityRelationship row
 * returned by the internal CRUD API and by
 * {@code GET /api/v1/identities/{id}?fetch=relationships}. Spec §6.2 / §7
 * locks the shape to {@code {id, displayName, identityType}} — frontend MUST
 * render {@code displayName} as is (it is the Identity's own displayName, not
 * the underlying Party's name; multi-employment safe).
 */
export interface IdentityRelationshipTargetSummary {
  id: UUID;
  displayName: string;
  identityType: string;
}

/**
 * IdentityRelationship row as returned by the internal CRUD API
 * ({@code /api/i/identity-relationships}) and by the inline {@code relationships}
 * field on the public Identity GET ({@code /api/v1/identities/{id}?fetch=relationships}).
 *
 * <p>The {@code responsibilities} list carries the responsibility codes on the
 * relationship type's schema row (e.g. {@code ["APPROVAL", "CERTIFICATION",
 * "REHIRE_REVIEW", "DELEGATION"]} for MANAGED_BY). Empty when the schema row
 * carries no responsibilities. Per spec §7 the frontend renders
 * {@code toIdentity.displayName} exclusively — no fall-back to
 * {@code toIdentity.party.displayName}.
 */
export interface IdentityRelationship {
  id: UUID;
  fromIdentityId: UUID;
  toIdentity: IdentityRelationshipTargetSummary;
  type: string;
  responsibilities: string[];
  validFrom: Instant;
  validTo: Instant | null;
  version: number;
}

/**
 * Request to create a new Identity-to-Identity relationship via the internal
 * CRUD API ({@code POST /api/i/identity-relationships}). Mirrors backend
 * {@code IdentityRelationshipCreateRequest}.
 */
export interface IdentityRelationshipCreateRequest {
  fromIdentityId: UUID;
  toIdentityId: UUID;
  relationshipType: string;
  validFrom?: Instant | null;
  validTo?: Instant | null;
}

/**
 * Why a lifecycle transition occurred. Mirrors backend
 * com.irian.iam.shared.lifecycle.TriggerSource (spec §4.4 of
 * docs/specs/lifecycle-events/architecture.md).
 */
export const TriggerSource = {
    USER_ACTION: 'USER_ACTION',
    JML_JOINER: 'JML_JOINER',
    JML_LEAVER: 'JML_LEAVER',
    JML_MOVER: 'JML_MOVER',
    EXIT_DATE_JOB: 'EXIT_DATE_JOB',
    REACTIVATION_SWEEPER: 'REACTIVATION_SWEEPER',
    INBOUND_SYNC: 'INBOUND_SYNC',
    OUTBOUND_PROVISIONING: 'OUTBOUND_PROVISIONING',
    ACCESS_REQUEST: 'ACCESS_REQUEST',
    CERTIFICATION_REVOKE: 'CERTIFICATION_REVOKE',
    INITIAL_LOAD: 'INITIAL_LOAD',
    SYSTEM: 'SYSTEM',
} as const;
export type TriggerSource = typeof TriggerSource[keyof typeof TriggerSource];

/**
 * Inline actor projection on a lifecycle event response — null when the event
 * was system-driven (spec §3.4 — JML/sync/sweeper/INITIAL_LOAD have no human
 * actor). Resolved server-side via LEFT JOIN against {@code auth_users}.
 */
export interface IdentityLifecycleEventActor {
  id: UUID;
  displayName: string;
}

/**
 * One row of the {@code identity_lifecycle_events} timeline. Append-only —
 * spec §4.1, §7.1. Returned by
 * {@code GET /api/i/identities/{id}/lifecycle-events}.
 *
 * <p>The persisted entity carries {@code actorId}; the API response carries
 * an {@code actor: {id, displayName}} object resolved via JOIN. The mock-mode
 * fixture (which mirrors the DB shape one-for-one) carries {@code actorId} too;
 * the wire DTO returns the {@code actor} object instead.
 */
export interface IdentityLifecycleEvent {
  id: UUID;
  identityId: UUID;
  fromState: IdentityLifecycleState | null;
  toState: IdentityLifecycleState;
  changedAt: Instant;
  actor: IdentityLifecycleEventActor | null;
  triggerSource: TriggerSource;
  triggerRefType: string | null;
  triggerRefId: UUID | null;
  reason: string | null;
}

/**
 * Rich enum carrying UI rendering metadata (badge variant + icon) for
 * {@link IdentityLifecycleState} wire values. The wire-type string-union
 * remains the shape used by API responses; this class is the single source
 * of truth for "how does this state look in the UI". The icon supports the
 * color-independent status display contract (WCAG 1.4.1).
 *
 * Localized labels for these states live in app-config (see
 * {@code useAppConfig().lifecycleStates}) and are resolved at render time
 * via {@code labelOf(lifecycleStates, state)} — no label lambda here.
 */
export class IdentityLifecycleStateBadge extends EnumValue {
    constructor(
        code: string,
        variant: BadgeVariant,
        public readonly icon: LucideIcon,
    ) {
        super(code, variant)
    }

    static readonly ACTIVE = new IdentityLifecycleStateBadge(
        IdentityLifecycleState.ACTIVE, BadgeVariant.SUCCESS, CheckCircle,
    )
    static readonly SUSPENDED = new IdentityLifecycleStateBadge(
        IdentityLifecycleState.SUSPENDED, BadgeVariant.WARNING, AlertCircle,
    )
    static readonly TERMINATED = new IdentityLifecycleStateBadge(
        IdentityLifecycleState.TERMINATED, BadgeVariant.DESTRUCTIVE, XCircle,
    )

    static readonly values: ReadonlyArray<IdentityLifecycleStateBadge> = [
        IdentityLifecycleStateBadge.ACTIVE,
        IdentityLifecycleStateBadge.SUSPENDED,
        IdentityLifecycleStateBadge.TERMINATED,
    ]

    static of(code: IdentityLifecycleState | null | undefined): IdentityLifecycleStateBadge | undefined {
        return IdentityLifecycleStateBadge.values.find((v) => v.code === code)
    }
}

/**
 * Get available lifecycle actions for current state. The narrowed return type
 * lets callers pass results straight into {@link IdentityLifecycleActionTarget}-typed
 * resolvers without a cast.
 */
export function getAvailableLifecycleActions(state: IdentityLifecycleState): IdentityLifecycleActionTarget[] {
  switch (state) {
    case IdentityLifecycleState.ACTIVE:
      return [IdentityLifecycleState.SUSPENDED, IdentityLifecycleState.TERMINATED];
    case IdentityLifecycleState.SUSPENDED:
      return [IdentityLifecycleState.ACTIVE, IdentityLifecycleState.TERMINATED];
    case IdentityLifecycleState.TERMINATED:
      return [];
  }
}

/* eslint-disable i18next/no-literal-string */
export const LifecycleEventType = {
    ACTIVATION: 'ACTIVATION',
    TERMINATION: 'TERMINATION',
    REHIRE: 'REHIRE',
} as const
export type LifecycleEventType = typeof LifecycleEventType[keyof typeof LifecycleEventType]
/* eslint-enable i18next/no-literal-string */

/**
 * Upcoming lifecycle event response from the API.
 */
export interface UpcomingLifecycleEvent {
  identity: EntityLink;
  lifecycleState: IdentityLifecycleState;
  eventType: LifecycleEventType;
  eventDate: string;
  entryDate: Instant | null;
  exitDate: Instant | null;
}

/**
 * Identifier response from the API.
 */
export interface Identifier {
  id: UUID;
  identifierType: string;
  identifierValue: string;
  primary: boolean;
  validFrom: Instant;
  validTo: Instant | null;
}

/**
 * Identity attribute value types.
 */
import {AttributeValueType} from './attribute'

export interface IdentityAttribute extends AttributeValue {
  observedAt: Instant;
}

export function formatAttributeValue(attr: IdentityAttribute): string {
  if (attr.values && attr.values.length > 0) {
    return attr.values.join(', ');
  }
  return AttributeValueType.format(attr.value, attr.valueType);
}

/* eslint-disable i18next/no-literal-string -- field name constants for backend API */
export const IdentityCreateRequestFields = {
    partyId: 'partyId',
    identityType: 'identityType',
    entryDate: 'entryDate',
    exitDate: 'exitDate',
    attributes: 'attributes',
} as const

export const IdentityLifecycleRequestFields = {
    lifecycleState: 'lifecycleState',
    version: 'version',
    reactivationDate: 'reactivationDate',
    reason: 'reason',
} as const

export const UpdateReactivationDateRequestFields = {
    reactivationDate: 'reactivationDate',
    version: 'version',
} as const

export const IdentityResponseFields = {
    id: 'id',
    party: 'party',
    identityType: 'identityType',
    lifecycleState: 'lifecycleState',
    lifecycleChangedAt: 'lifecycleChangedAt',
    entryDate: 'entryDate',
    exitDate: 'exitDate',
    reactivationDate: 'reactivationDate',
    derivedDisplayName: 'derivedDisplayName',
    sources: 'sources',
    version: 'version',
    createdAt: 'createdAt',
    updatedAt: 'updatedAt',
} as const

export const IdentitySnapshotResponseFields = {
    asOf: 'asOf',
    snapshot: 'snapshot',
} as const

export const IdentityLifecycleEventResponseFields = {
    id: 'id',
    identityId: 'identityId',
    fromState: 'fromState',
    toState: 'toState',
    changedAt: 'changedAt',
    actor: 'actor',
    triggerSource: 'triggerSource',
    triggerRefType: 'triggerRefType',
    triggerRefId: 'triggerRefId',
    reason: 'reason',
} as const

export const UpcomingLifecycleEventResponseFields = {
    identity: 'identity',
    lifecycleState: 'lifecycleState',
    eventType: 'eventType',
    eventDate: 'eventDate',
    entryDate: 'entryDate',
    exitDate: 'exitDate',
} as const

export const UpdateEntryDateRequestFields = {
    entryDate: 'entryDate',
    version: 'version',
} as const

export const UpdateExitDateRequestFields = {
    exitDate: 'exitDate',
    version: 'version',
} as const

export const RehireRequestFields = {
    newEntryDate: 'newEntryDate',
    reason: 'reason',
    version: 'version',
} as const

export const IdentityFilterParams = {
    identityType: 'identityType',
    lifecycleState: 'lifecycleState',
} as const
/* eslint-enable i18next/no-literal-string */
